import React, { useEffect, useState } from 'react';
import { useCart } from '../contexts/CartContext';
import { Link } from 'react-router-dom';
import { useCurrency } from '../contexts/CurrencyContext';
import toast from 'react-hot-toast';
import { 
  TrashIcon, 
  PlusIcon, 
  MinusIcon,
  ShoppingBagIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const Cart = () => {
  const { cartItems, loading, updateCartItem, removeFromCart, getCartTotal } = useCart();
  const { formatPrice } = useCurrency();
  const [updatingItems, setUpdatingItems] = useState({});
  const [shippingConfig, setShippingConfig] = useState({ enabled: true, flatCharge: 500, freeOver: 5000 });

  useEffect(() => {
    const loadShippingConfig = async () => {
      try {
        // Prefer the same endpoint checkout uses so both pages show identical shipping.
        let response = await fetch('/api/settings');
        let data = null;

        if (response.ok) {
          data = await response.json();
        } else {
          // Backward-compatible fallback for environments that expose only public settings.
          response = await fetch('/api/settings/public');
          if (response.ok) {
            data = await response.json();
          }
        }

        if (!response.ok || !data) {
          return;
        }

        const value = data?.data?.settings?.shipping_config?.value;
        if (value && typeof value === 'object') {
          setShippingConfig(prev => ({
            ...prev,
            enabled: value.enabled !== undefined ? Boolean(value.enabled) : prev.enabled,
            flatCharge: Number.isFinite(Number(value.flatCharge)) ? Number(value.flatCharge) : prev.flatCharge,
            freeOver: Number.isFinite(Number(value.freeOver)) ? Number(value.freeOver) : prev.freeOver
          }));
        }
      } catch (error) {
        console.warn('Failed to load shipping config for cart:', error?.message || error);
      }
    };

    loadShippingConfig();
  }, []);

  const handleQuantityChange = async (cartItemId, newQuantity) => {
    if (newQuantity <= 0) return;
    
    setUpdatingItems(prev => ({ ...prev, [cartItemId]: true }));
    const result = await updateCartItem(cartItemId, newQuantity);
    setUpdatingItems(prev => ({ ...prev, [cartItemId]: false }));
    
    if (result.success) {
      toast.success('Cart updated');
    } else {
      toast.error(result.message || 'Failed to update cart');
    }
  };

  const handleRemoveItem = async (cartItemId, productName) => {
    console.log('🗑️ [Cart] handleRemoveItem called:', { cartItemId, productName, itemIdType: typeof cartItemId });
    
    // Validate cart item ID
    if (!cartItemId || cartItemId === 'undefined' || cartItemId === 'null') {
      console.error('❌ [Cart] Invalid cart item ID:', cartItemId);
      toast.error('Invalid cart item. Please refresh the page and try again.');
      return;
    }
    
    const displayName = productName || 'this item';
    if (window.confirm(`Remove ${displayName} from cart?`)) {
      const result = await removeFromCart(cartItemId);
      if (result.success) {
        toast.success('Item removed from cart');
      } else {
        toast.error(result.message || 'Failed to remove item');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-block">
            <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
            <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin absolute top-0 left-0 animate-pulse"></div>
          </div>
          <p className="mt-4 text-gray-600">Loading your cart...</p>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center bg-white p-12 rounded-2xl shadow-xl max-w-md">
          <ShoppingBagIcon className="w-24 h-24 text-gray-300 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Your Cart is Empty</h1>
          <p className="text-gray-600 mb-8">Start shopping to add items to your cart!</p>
          <Link
            to="/products"
            className="inline-block bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-8 py-4 rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 shadow-lg font-semibold"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  const subtotal = getCartTotal();
  const freeOver = Number(shippingConfig.freeOver) || 0;
  const flatCharge = Number(shippingConfig.flatCharge) || 0;
  const shipping = !shippingConfig.enabled
    ? 0
    : freeOver > 0 && subtotal >= freeOver
      ? 0
      : Math.max(0, flatCharge);
  const total = subtotal + shipping;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Shopping Cart</h1>
            <p className="text-gray-600">{cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}</p>
          </div>
          <Link
            to="/products"
            className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-semibold"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Continue Shopping
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => {
              // Debug: Log each item to verify it has an ID
              if (!item.id) {
                console.warn('⚠️ [Cart] Cart item missing ID:', item);
              }
              return (
              <div key={item.id || item.product_id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="p-6">
                  <div className="flex flex-col sm:flex-row gap-6">
                    {/* Product Image */}
                    <Link to={`/products/${item.product_id}`} className="flex-shrink-0">
                      <img
                        src={item.image_url || '/api/placeholder/150/150'}
                        alt={item.name || 'Product'}
                        className="w-32 h-32 object-cover rounded-xl hover:scale-105 transition-transform duration-300"
                      />
                    </Link>

                    {/* Product Info */}
                    <div className="flex-1">
                      <Link to={`/products/${item.product_id}`}>
                        <h3 className="text-xl font-bold text-gray-900 mb-2 hover:text-indigo-600 transition-colors">
                          {item.name || 'Product'}
                        </h3>
                      </Link>
                      <p className="text-lg font-semibold text-indigo-600 mb-4">
                        {formatPrice(item.price)} each
                      </p>

                      {/* Quantity Controls */}
                      <div className="flex items-center gap-4 mb-4">
                        <label className="text-sm font-semibold text-gray-700">Quantity:</label>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                            disabled={updatingItems[item.id] || item.quantity <= 1}
                            className="w-10 h-10 border-2 border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                          >
                            <MinusIcon className="w-5 h-5" />
                          </button>
                          <span className="w-12 text-center font-semibold text-lg">
                            {updatingItems[item.id] ? '...' : item.quantity}
                          </span>
                          <button
                            onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                            disabled={updatingItems[item.id]}
                            className="w-10 h-10 border-2 border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                          >
                            <PlusIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => handleRemoveItem(item.id, item.name)}
                          className="flex items-center gap-2 text-red-600 hover:text-red-700 font-semibold transition-colors"
                        >
                          <TrashIcon className="w-5 h-5" />
                          Remove
                        </button>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900 mb-2">
                        {formatPrice(item.price * item.quantity)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatPrice(item.price)} × {item.quantity}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-gray-700">
                  <span>Subtotal ({cartItems.reduce((sum, item) => sum + item.quantity, 0)} items):</span>
                  <span className="font-semibold">{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Shipping:</span>
                  <span className="font-semibold">
                    {shipping === 0 ? (
                      <span className="text-green-600">Free</span>
                    ) : (
                      formatPrice(shipping)
                    )}
                  </span>
                </div>
                {shippingConfig.enabled && freeOver > 0 && subtotal < freeOver && (
                  <div className="text-sm text-indigo-600 bg-indigo-50 p-3 rounded-lg">
                    💡 Add {formatPrice(freeOver - subtotal)} more for free shipping!
                  </div>
                )}
                <div className="border-t-2 border-gray-200 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold text-gray-900">Total:</span>
                    <span className="text-2xl font-bold text-indigo-600">{formatPrice(total)}</span>
                  </div>
                </div>
              </div>
              <Link
                to="/checkout"
                className="block w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-center py-4 rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl font-semibold text-lg"
              >
                Proceed to Checkout
              </Link>
              <Link
                to="/products"
                className="block w-full text-center py-3 text-gray-600 hover:text-indigo-600 font-semibold mt-4"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
